import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { TestUserManager } from "@/components/debug/TestUserManager";
import { supabase } from "@/lib/supabase";
import { Loader2, Wrench, Eye, EyeOff } from "lucide-react";

function formatDocumento(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9)
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  const cnpjDigits = digits.slice(0, 14);
  if (cnpjDigits.length <= 2) return cnpjDigits;
  if (cnpjDigits.length <= 5)
    return `${cnpjDigits.slice(0, 2)}.${cnpjDigits.slice(2)}`;
  if (cnpjDigits.length <= 8)
    return `${cnpjDigits.slice(0, 2)}.${cnpjDigits.slice(2, 5)}.${cnpjDigits.slice(5)}`;
  if (cnpjDigits.length <= 12)
    return `${cnpjDigits.slice(0, 2)}.${cnpjDigits.slice(2, 5)}.${cnpjDigits.slice(5, 8)}/${cnpjDigits.slice(8)}`;
  return `${cnpjDigits.slice(0, 2)}.${cnpjDigits.slice(2, 5)}.${cnpjDigits.slice(5, 8)}/${cnpjDigits.slice(8, 12)}-${cnpjDigits.slice(12)}`;
}

export default function Login() {
  const [loginMode, setLoginMode] = useState<"email" | "documento">("email");
  const [email, setEmail] = useState("");
  const [documento, setDocumento] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const isDebugMode = import.meta.env.DEV || (typeof window !== "undefined" && (window as any).__DEBUG_MODE);

  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/dashboard");
    }
  }, [authLoading, user, setLocation]);

  const performLogin = async (loginEmail: string, loginPassword: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (error) throw error;
      setLocation("/dashboard");
    } catch (err: any) {
      let message: string;
      if (err.message === "Invalid login credentials") {
        message = "E-mail ou senha incorretos";
      } else if (err.message === "Email not confirmed") {
        message = "E-mail não confirmado. Verifique sua caixa de entrada.";
      } else if (err.message === "User not found") {
        message = "Usuário não encontrado";
      } else if (err.message === "Too many requests") {
        message = "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
      } else {
        message = err.message || "Erro ao fazer login";
      }
      setError(message);
      toast({
        title: "Erro de Autenticação",
        description: message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (loginMode === "documento") {
        const cleanDoc = documento.replace(/\D/g, "");
        if (cleanDoc.length !== 11 && cleanDoc.length !== 14) {
          throw new Error("Documento ou senha incorretos.");
        }
        const res = await fetch("/api/auth/login-documento", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ documento: cleanDoc, senha: password }),
        });
        if (res.status === 429) {
          throw new Error("Muitas tentativas. Aguarde um pouco e tente novamente.");
        }
        if (!res.ok) throw new Error("Documento ou senha incorretos.");
        const data = await res.json();
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        if (sessionError) throw new Error("Documento ou senha incorretos.");
        setLocation("/dashboard");
      } else {
        await performLogin(email, password);
      }
    } catch (err: any) {
      if (loginMode === "email") return;
      const message =
        err.message === "Muitas tentativas. Aguarde um pouco e tente novamente."
          ? err.message
          : "Documento ou senha incorretos.";
      setError(message);
      toast({
        title: "Erro de Autenticação",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestUserLogin = async (testEmail: string, testPassword: string) => {
    setLoading(true);
    setError("");
    try {
      await performLogin(testEmail, testPassword);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      {isDebugMode && (
        <div className="fixed top-4 right-4 z-50">
          <TestUserManager
            onSelectUser={handleTestUserLogin}
            isLoading={loading}
          />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-xl shadow-primary/30 rotate-3">
            <Wrench className="w-8 h-8 text-white" />
          </div>
        </div>

        <Card className="p-8 border-none shadow-2xl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold font-display text-slate-900">
              Bem-vindo de volta
            </h1>
            <p className="text-muted-foreground mt-2">
              Insira suas credenciais para acessar o painel
            </p>
          </div>

          <div
            className="flex rounded-lg bg-slate-100 p-1 mb-6"
            data-testid="login-mode-toggle"
          >
            <button
              type="button"
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${
                loginMode === "email"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => {
                setLoginMode("email");
                setError("");
              }}
              data-testid="toggle-email"
            >
              E-mail
            </button>
            <button
              type="button"
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${
                loginMode === "documento"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => {
                setLoginMode("documento");
                setError("");
              }}
              data-testid="toggle-documento"
            >
              CPF / CNPJ
            </button>
          </div>

          <form
            onSubmit={handleLogin}
            className="space-y-4"
            data-testid="form-login"
          >
            {loginMode === "email" ? (
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-50 border-slate-200"
                  data-testid="input-email"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="documento">CPF ou CNPJ</Label>
                <Input
                  id="documento"
                  type="text"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  value={documento}
                  onChange={(e) =>
                    setDocumento(formatDocumento(e.target.value))
                  }
                  required
                  className="bg-slate-50 border-slate-200"
                  data-testid="input-documento"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-50 border-slate-200 pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="text-right -mt-2">
              <a
                href="/esqueci-senha"
                className="text-sm text-primary hover:underline"
                data-testid="link-esqueci-senha"
              >
                Esqueci minha senha
              </a>
            </div>

            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="text-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-base shadow-lg shadow-primary/25 mt-4"
              disabled={loading}
              data-testid="button-login"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Entrar
            </Button>
          </form>

          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              Ainda não tem conta?{" "}
              <a
                href="/registrar"
                className="text-primary hover:underline font-medium"
                data-testid="link-registrar"
              >
                Cadastre-se aqui
              </a>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
